// charts.tsx — inline SVG chart primitives for the console.
//
// No charting library. The prototype draws everything as inline SVG and the app
// has no chart dependency; adding one for six small visuals would be a large
// bundle cost for the operator surface alone. These are deliberately small and
// take plain arrays.
//
// EVERY chart renders an explicit empty state rather than an empty axis. A blank
// chart and a chart of zeros look identical at a glance, and on this surface that
// difference decides whether someone investigates.

import React from "react";
import { PT, PT_NUM } from "../../platformTheme";

// ── Sparkline ────────────────────────────────────────────────────────────────

export const Sparkline: React.FC<{
  points: number[]; w?: number; h?: number; color?: string; fill?: boolean;
}> = ({ points, w = 100, h = 26, color = PT.chBlue, fill = false }) => {
  if (!points || points.length < 2) {
    return <span style={{ fontSize: 10.5, color: PT.txD }}>—</span>;
  }
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const span = max - min || 1;
  const step = w / (points.length - 1);
  const y = (v: number) => h - 2 - ((v - min) / span) * (h - 4);
  const d = points.map((v, i) => `${i === 0 ? "M" : "L"}${(i * step).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  return (
    <svg width={w} height={h} style={{ display: "block", overflow: "visible" }} aria-hidden>
      {fill && <path d={`${d} L${w},${h} L0,${h} Z`} fill={color} opacity={0.14} />}
      <path d={d} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

// ── Area + line chart (Platform activity) ────────────────────────────────────

export interface SeriesDef { label: string; color: string; values: number[]; dashed?: boolean }

export const AreaChart: React.FC<{ series: SeriesDef[]; height?: number; labels?: string[] }> = ({
  series, height = 170, labels,
}) => {
  const usable = (series || []).filter((s) => s.values && s.values.length > 1);
  if (!usable.length) {
    return (
      <div style={{ height, display: "flex", alignItems: "center", justifyContent: "center", color: PT.txD, fontSize: 12 }}>
        No activity recorded in this window.
      </div>
    );
  }
  const w = 1120;
  const n = Math.max(...usable.map((s) => s.values.length));
  const max = Math.max(1, ...usable.flatMap((s) => s.values));
  const step = w / (n - 1);
  const y = (v: number) => height - 12 - (v / max) * (height - 26);
  const path = (vals: number[]) =>
    vals.map((v, i) => `${i === 0 ? "M" : "L"}${(i * step).toFixed(1)},${y(v).toFixed(1)}`).join(" ");

  return (
    <div style={{ overflowX: "auto" }}>
      <svg viewBox={`0 0 ${w} ${height}`} width="100%" height={height} preserveAspectRatio="none" role="img">
        {[0, 0.25, 0.5, 0.75, 1].map((f) => (
          <line key={f} x1={0} x2={w} y1={y(max * f)} y2={y(max * f)} stroke={PT.chGrid} strokeWidth={1} />
        ))}
        {usable.map((s, i) => (
          <g key={s.label}>
            {!s.dashed && <path d={`${path(s.values)} L${w},${height} L0,${height} Z`} fill={s.color} opacity={0.16} />}
            <path
              d={path(s.values)}
              fill="none"
              stroke={s.color}
              strokeWidth={i === 0 ? 2 : 1.6}
              strokeDasharray={s.dashed ? "5 4" : undefined}
              strokeLinejoin="round"
            />
          </g>
        ))}
      </svg>
      <div style={{ display: "flex", gap: 16, marginTop: 8, flexWrap: "wrap" }}>
        {usable.map((s) => (
          <span key={s.label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: PT.txM }}>
            <span style={{ width: 14, height: 0, borderTop: `2px ${s.dashed ? "dashed" : "solid"} ${s.color}` }} />
            {s.label}
          </span>
        ))}
      </div>
      {labels && labels.length >= 2 && (
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10.5, color: PT.txD, marginTop: 2 }}>
          <span>{labels[0]}</span><span>{labels[labels.length - 1]}</span>
        </div>
      )}
    </div>
  );
};

// ── Donut (Customer status) ──────────────────────────────────────────────────

export interface DonutSeg { label: string; value: number; color: string }

export const Donut: React.FC<{ segments: DonutSeg[]; size?: number; centerLabel?: string }> = ({
  segments, size = 128, centerLabel,
}) => {
  const total = segments.reduce((a, s) => a + (s.value || 0), 0);
  const r = 52;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
      <div style={{ position: "relative", width: size, height: size, flex: "none" }}>
        <svg width={size} height={size} viewBox="0 0 132 132" role="img">
          <circle cx="66" cy="66" r={r} fill="none" stroke={PT.chGrid} strokeWidth="14" />
          {total > 0 && segments.map((s) => {
            const len = (s.value / total) * circ;
            const el = (
              <circle
                key={s.label}
                cx="66" cy="66" r={r} fill="none" stroke={s.color} strokeWidth="14"
                strokeDasharray={`${len} ${circ - len}`}
                strokeDashoffset={-offset}
                transform="rotate(-90 66 66)"
              />
            );
            offset += len;
            return el;
          })}
        </svg>
        <div style={{
          position: "absolute", inset: 0, display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", pointerEvents: "none",
        }}>
          <div style={{ ...PT_NUM, fontSize: 21, fontWeight: 600 }}>{total}</div>
          {centerLabel && <div style={{ fontSize: 10, color: PT.txD }}>{centerLabel}</div>}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        {segments.map((s) => (
          <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
            <span style={{ width: 9, height: 9, borderRadius: 5, background: s.color, flex: "none" }} />
            <span style={{ color: PT.txM, minWidth: 78 }}>{s.label}</span>
            <span style={{ ...PT_NUM, fontWeight: 600 }}>{s.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Progress bar (server metrics) ────────────────────────────────────────────

export const ProgressBar: React.FC<{ pct: number | null | undefined }> = ({ pct }) => {
  if (pct == null || !Number.isFinite(Number(pct))) return null;
  const v = Math.max(0, Math.min(100, Number(pct)));
  // Colour by pressure, not decoration: an operator should see a hot disk
  // without reading the number.
  const color = v >= 90 ? PT.err : v >= 75 ? PT.warn : PT.ok;
  return (
    <div style={{ height: 5, background: PT.chGrid, borderRadius: 3, overflow: "hidden" }}>
      <div style={{ width: `${v}%`, height: "100%", background: color, borderRadius: 3 }} />
    </div>
  );
};

// ── Horizontal bar list (coverage / spend by X) ──────────────────────────────

export const BarList: React.FC<{
  rows: { label: string; value: number; sub?: string; color?: string; onClick?: () => void }[];
  format?: (v: number) => string;
}> = ({ rows, format }) => {
  if (!rows.length) return <div style={{ fontSize: 12, color: PT.txD, padding: "10px 0" }}>Nothing to show.</div>;
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {rows.map((r) => (
        <div key={r.label} onClick={r.onClick} style={{ cursor: r.onClick ? "pointer" : "default" }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
            <span style={{ color: PT.tx }}>{r.label}</span>
            <span style={{ ...PT_NUM, color: PT.txM }}>{format ? format(r.value) : r.value}{r.sub ? ` · ${r.sub}` : ""}</span>
          </div>
          <div style={{ height: 7, background: PT.chGrid, borderRadius: 4, overflow: "hidden" }}>
            <div style={{ width: `${(r.value / max) * 100}%`, height: "100%", background: r.color || PT.chBlue, borderRadius: 4 }} />
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * NOT-YET-CAPTURED marker.
 *
 * Used wherever the design asks for a number the platform does not record. It is
 * deliberately loud and never renders a plausible-looking zero — the whole point
 * is that an operator can tell "nothing happened" from "we don't measure this".
 */
export const NotCaptured: React.FC<{ what: string; needs: string }> = ({ what, needs }) => (
  <div style={{
    border: `1px dashed ${PT.brdStrong}`, borderRadius: PT.radiusLg, padding: "13px 15px",
    background: PT.bgInset, color: PT.txM, fontSize: 12.5, lineHeight: 1.55,
  }}>
    <div style={{ fontWeight: 600, color: PT.tx, marginBottom: 3 }}>{what} — not captured yet</div>
    <div>{needs}</div>
  </div>
);
